import { Injectable } from '@angular/core';
import { Order, QueryConfig, QueryEntity } from '@datorama/akita';
import moment, { Moment } from 'moment/moment';
import { map, Observable } from 'rxjs';
import { FantasyGameState, FantasyGameStore } from './fantasy-game.store';
import { FantasyGame } from './models/fantasy-game.model';
import { GameStatus } from './models/game-status.enum';
import { FantasyPlayer } from './models/fantasy-player.model';

@Injectable({ providedIn: 'root' })
@QueryConfig({ sortBy: 'EndDate', sortByOrder: Order.ASC })
export class FantasyGameQuery extends QueryEntity<FantasyGameState> {
  pendingPeriod: number = 48;

  constructor(protected store: FantasyGameStore) {
    super(store);
  }

  /**
   * Get all fantasy games
   */
  selectGames() {
    return this.selectAll();
  }

  /**
   * Get fantasy game by id
   */
  selectGameById(id: number): Observable<FantasyGame | undefined> {
    return this.selectEntity(id);
  }

  /**
   * Gets players for a given game
   * @param gameId game id to filter on
   */
  selectPlayersByGameId(gameId: number): Observable<FantasyPlayer[] | undefined> {
    return this.selectEntity(gameId, (game) => game?.GamePlayers);
  }

  /**
   * Get the selected player for the fantasy game
   * @param gameId game id to filter on
   */
  getSelectedPlayer(gameId: number): number | undefined {
    return this.getEntity(gameId)?.SelectedPlayerId;
  }

  /**
   * Get current games
   */
  selectCurrentGames() {
    return this.selectAll({
      filterBy: (game) => {
        return moment().isBetween(moment.utc(game.StartDate), moment.utc(game.EndDate)) && game.Status != GameStatus.Processed;
      },
    });
  }

  /**
   * Gets all finished fantasy games
   */
  selectFinishedGames() {
    return this.selectAll({
      filterBy: (game) => moment.utc(game.EndDate).isBefore(moment.utc()),
    });
  }

  /**
   * Get all finished games after given date
   */
  selectFinshedFantasyGamesAfterDate(date: Date | Moment) {
    return this.selectFinishedGames().pipe(
      map((items) =>
        items.filter((item) => {
          return moment.utc(item.EndDate).isAfter(date);
        })
      )
    );
  }
}
